import { useState, useRef } from 'react';
import { submitMessage, uploadImage } from '../api';

const DOMAIN = '@auspayplus.com.au';

interface Props {
  onClose: () => void;
}

export function SubmitForm({ onClose }: Props) {
  const [from, setFrom] = useState('');
  const [localPart, setLocalPart] = useState('');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return; }
    setError(null);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = localPart.trim() + DOMAIN;
    setSubmitting(true);
    setError(null);
    try {
      let image_url: string | undefined;
      if (imageFile) image_url = await uploadImage(imageFile);
      await submitMessage({ from, email, message, image_url });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
        <div className="bg-[#1a1a2e] rounded-2xl p-8 max-w-md w-full text-center border border-[#2d2d4e]">
          <div className="text-5xl mb-4">✉️</div>
          <h2 className="text-2xl font-bold text-violet-400 mb-2">Check your email!</h2>
          <p className="text-slate-400">We've sent you a link to publish your message on the card.</p>
          <button
            onClick={onClose}
            className="mt-6 px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
          >
            Back to card
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1a1a2e] rounded-2xl p-6 max-w-lg w-full border border-[#2d2d4e] max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-violet-400">Add your message</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-3xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Your name</label>
            <input
              value={from}
              onChange={e => setFrom(e.target.value)}
              required
              className="w-full bg-[#0d0d1a] border border-[#2d2d4e] rounded-lg px-3 py-2 text-slate-200 focus:border-violet-500 focus:outline-none"
              placeholder="e.g. Dan Bryar"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Work email</label>
            <div className="flex items-center bg-[#0d0d1a] border border-[#2d2d4e] rounded-lg focus-within:border-violet-500 transition-colors">
              <input
                value={localPart}
                onChange={e => setLocalPart(e.target.value.replace(/@.*/, ''))}
                required
                className="flex-1 min-w-0 bg-transparent px-3 py-2 text-slate-200 focus:outline-none"
                placeholder="firstname.lastname"
              />
              <span className="px-3 py-2 text-slate-500 select-none whitespace-nowrap">{DOMAIN}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Message <span className="text-slate-600">(markdown supported)</span>
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              required
              rows={6}
              className="w-full bg-[#0d0d1a] border border-[#2d2d4e] rounded-lg px-3 py-2 text-slate-200 focus:border-violet-500 focus:outline-none resize-none font-mono text-sm"
              placeholder="Write your message here. **Bold**, _italic_, and more are supported!"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Image <span className="text-slate-600">(optional — memes welcome, max 5MB)</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFile}
              className="hidden"
            />
            {!imagePreview ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="px-4 py-2 border border-dashed border-[#2d2d4e] rounded-lg text-slate-500 hover:border-violet-500 hover:text-violet-400 transition-colors text-sm"
              >
                + Attach an image
              </button>
            ) : (
              <div className="relative inline-block">
                <img src={imagePreview} alt="Preview" className="h-28 rounded-lg object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm leading-none"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-[#2d2d4e] text-slate-400 rounded-lg hover:border-slate-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !localPart.trim()}
              className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white rounded-lg transition-colors font-medium"
            >
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
